/** Fuseau civil contractuel des périodes administratives, indépendant du poste opérateur. */
export const ADMIN_TIME_ZONE = 'Europe/Paris';

/** Une lecture historique admin peut aller jusqu'à 31 jours civils Paris depuis son heure de départ. */
export const MAX_ADMIN_TIME_RANGE_CALENDAR_DAYS = 31;

/** Budget maximum de payload/rendu commun aux graphiques administratifs à buckets. */
export const MAX_ADMIN_TIME_RANGE_POINTS = 5_000;

export const MIN_ADMIN_GPU_BUCKET_SECONDS = 1;
export const MAX_ADMIN_GPU_BUCKET_SECONDS = 24 * 3600;

export interface AdminParisDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

export interface AdminParisCivilDate {
  year: number;
  month: number;
  day: number;
}

const ADMIN_PARIS_CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const PARIS_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: ADMIN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function numericPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const value = parts.find((part) => part.type === type)?.value;
  if (!value) throw new Error(`La date ${ADMIN_TIME_ZONE} ne contient pas la partie ${type}.`);
  return Number(value);
}

function daysInAdminCivilMonth(year: number, month: number): number {
  if (month === 2) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** Refuse les dates que l'API Date normaliserait silencieusement vers un autre jour. */
export function parseAdminParisCivilDate(value: string): AdminParisCivilDate | null {
  const match = ADMIN_PARIS_CIVIL_DATE_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInAdminCivilMonth(year, month)) return null;
  return { year, month, day };
}

function formatAdminCivilDate(parts: AdminParisCivilDate): string {
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

/**
 * `Date.UTC` réécrit les années 0..99 en 1900..1999. Toute frontière civile
 * admin doit donc passer par `setUTCFullYear`, comme l'ordinal de période.
 */
function utcEpochFromAdminDateTime(parts: AdminParisDateTimeParts): number {
  const date = new Date(0);
  date.setUTCHours(parts.hour, parts.minute, parts.second, parts.millisecond);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  return date.getTime();
}

function isSameAdminParisWallClock(left: AdminParisDateTimeParts, right: AdminParisDateTimeParts): boolean {
  return left.year === right.year
    && left.month === right.month
    && left.day === right.day
    && left.hour === right.hour
    && left.minute === right.minute
    && left.second === right.second
    && left.millisecond === right.millisecond;
}

/** Décompose un instant dans le calendrier Paris, sans jamais consulter le fuseau du navigateur. */
export function getAdminParisDateTimeParts(epochMs: number): AdminParisDateTimeParts {
  if (!Number.isFinite(epochMs)) {
    throw new Error(`L'instant admin Paris doit être fini en millisecondes, reçu ${epochMs}.`);
  }
  const parts = PARIS_PARTS_FORMATTER.formatToParts(new Date(epochMs));
  const rawHour = numericPart(parts, 'hour');

  return {
    year: numericPart(parts, 'year'),
    month: numericPart(parts, 'month'),
    day: numericPart(parts, 'day'),
    // Certains moteurs Intl rendent encore minuit sous la forme 24:00 malgré h23.
    hour: rawHour === 24 ? 0 : rawHour,
    minute: numericPart(parts, 'minute'),
    second: numericPart(parts, 'second'),
    // Intl ne rend pas les millisecondes ; elles restent identiques entre UTC et Paris.
    millisecond: new Date(epochMs).getUTCMilliseconds(),
  };
}

/** Retourne la date affichable Paris d'un instant, sans regarder le fuseau du navigateur/processus. */
export function getAdminParisCivilDate(epochMs: number): string {
  const { year, month, day } = getAdminParisDateTimeParts(epochMs);
  return formatAdminCivilDate({ year, month, day });
}

/**
 * Trouve l'instant UTC correspondant à minuit d'une date civile Paris. Les
 * offsets sont observés autour de la date puis validés par un aller-retour :
 * une soustraction fixe de 24 h ferait dériver les bornes aux changements DST.
 */
export function resolveAdminParisCivilDateStart(value: string): number | null {
  const date = parseAdminParisCivilDate(value);
  if (!date) return null;

  const target: AdminParisDateTimeParts = {
    ...date,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  };
  const naiveUtcMs = utcEpochFromAdminDateTime(target);
  const offsets = new Set<number>();
  for (let offsetHours = -36; offsetHours <= 36; offsetHours += 1) {
    const observedMs = naiveUtcMs + offsetHours * 3_600_000;
    const observedLocalAsUtcMs = utcEpochFromAdminDateTime(getAdminParisDateTimeParts(observedMs));
    offsets.add(observedLocalAsUtcMs - observedMs);
  }

  const matches = [...offsets]
    .map((offsetMs) => naiveUtcMs - offsetMs)
    .filter((candidateMs) => isSameAdminParisWallClock(getAdminParisDateTimeParts(candidateMs), target))
    .sort((left, right) => left - right);

  return matches.length === 1 ? matches[0] as number : null;
}

/** Ajoute des jours du calendrier civil, et non des multiples physiques de 24 h. */
export function addAdminParisCivilDays(value: string, days: number): string | null {
  const date = parseAdminParisCivilDate(value);
  if (!date || !Number.isSafeInteger(days)) return null;

  const utcDate = new Date(utcEpochFromAdminDateTime({
    ...date,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }));
  utcDate.setUTCDate(utcDate.getUTCDate() + days);
  return formatAdminCivilDate({
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  });
}

function parisDateOrdinal(parts: AdminParisDateTimeParts): number {
  // `Date.UTC(99, …)` interprète 99 comme 1999. `setUTCFullYear` conserve au
  // contraire le millésime ISO réel, donc une requête 0099 → 0100 ne peut pas
  // contourner le plafond SQL de 31 jours.
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  return date.getTime() / 86_400_000;
}

function parisTimeOfDayMilliseconds(parts: AdminParisDateTimeParts): number {
  return (((parts.hour * 60 + parts.minute) * 60 + parts.second) * 1000) + parts.millisecond;
}

/**
 * Mesure une période en jours civils Paris en conservant l'heure civile de
 * départ : `[1 jan. 00:01, 1 fév. 00:01)` vaut donc exactement 31 jours.
 *
 * Ce n'est volontairement pas le décompte des étiquettes de dates touchées.
 * Sinon la fenêtre visible « 30 jours » deviendrait invalide lors d'un DST
 * parce qu'une période physique de 30 × 24 h traverse parfois 31 dates Paris.
 */
export function countAdminParisCalendarDays(fromInclusiveMs: number, toExclusiveMs: number): number {
  if (
    !Number.isFinite(fromInclusiveMs)
    || !Number.isFinite(toExclusiveMs)
    || toExclusiveMs <= fromInclusiveMs
  ) {
    return 0;
  }

  const from = getAdminParisDateTimeParts(fromInclusiveMs);
  const toExclusive = getAdminParisDateTimeParts(toExclusiveMs);
  const dateDifference = parisDateOrdinal(toExclusive) - parisDateOrdinal(from);
  const extendsPastStartWallClock = parisTimeOfDayMilliseconds(toExclusive) > parisTimeOfDayMilliseconds(from);

  // Le max couvre notamment l'heure civile répétée à l'automne : deux
  // instants distincts peuvent alors partager la même heure Paris.
  return Math.max(1, dateDifference + (extendsPastStartWallClock ? 1 : 0));
}

export function isAdminRangeWithinCalendarDayLimit(
  fromInclusiveMs: number,
  toExclusiveMs: number,
  maxCalendarDays = MAX_ADMIN_TIME_RANGE_CALENDAR_DAYS,
): boolean {
  return Number.isInteger(maxCalendarDays)
    && maxCalendarDays > 0
    && toExclusiveMs > fromInclusiveMs
    && countAdminParisCalendarDays(fromInclusiveMs, toExclusiveMs) <= maxCalendarDays;
}

/** Le dernier bucket partiel est compté : il produit bien une ligne et du JSON. */
export function countAdminRangePoints(
  fromInclusiveMs: number,
  toExclusiveMs: number,
  bucketSeconds: number,
): number {
  return Math.ceil((toExclusiveMs - fromInclusiveMs) / (bucketSeconds * 1000));
}

export function isAdminRangeWithinPointBudget(
  fromInclusiveMs: number,
  toExclusiveMs: number,
  bucketSeconds: number,
  maxPoints = MAX_ADMIN_TIME_RANGE_POINTS,
): boolean {
  const durationMs = toExclusiveMs - fromInclusiveMs;
  return Number.isInteger(bucketSeconds)
    && bucketSeconds > 0
    && durationMs > 0
    && bucketSeconds * 1000 <= durationMs
    && countAdminRangePoints(fromInclusiveMs, toExclusiveMs, bucketSeconds) <= maxPoints;
}
